import { sdkRequest } from '@eventbrite/http';
import { gettext } from '@eventbrite/i18n';
import { createVenueAdapter, createVenueServiceDTOAdapter } from '../adapters';
import { FormValues, VenueDTO } from '../types';

/**
 * @deprecated Use `canCreateVenue` from `@eventbrite/event-create-ui` instead.
 * This function will be removed in a future version.
 * See: packages/organizer/event-create-ui/src/api/venue/createVenue.ts
 */
export const canCreateVenue = ({ venue }: FormValues) => {
    const { name, address } = venue;
    const { country, address1, city } = address || {};
    return !!(country && name && address1 && city);
};

/**
 * @deprecated Use `createVenue` from `@eventbrite/event-create-ui` instead.
 * This function will be removed in a future version.
 * The new version provides:
 * - Enhanced field-level error validation with ServiceError
 * - getMissingVenueFields utility for diagnostics
 * - Detailed error context for better UX
 * See: packages/organizer/event-create-ui/src/api/venue/createVenue.ts
 */
export const createVenue = async ({
    organizationId,
    values,
    geocodingSucceeded = false,
}: {
    organizationId: string;
    values: FormValues;
    geocodingSucceeded?: boolean;
}) => {
    if (!canCreateVenue(values)) {
        throw new Error('Not enough data to create a venue');
    }

    const data = {
        venue: createVenueServiceDTOAdapter(values.venue, geocodingSucceeded),
    };

    const options = {
        method: 'POST',
        body: JSON.stringify(data),
    };

    return sdkRequest(
        `/api/v3/organizations/${organizationId}/venues/`,
        options,
    )
        .then((venueDTO: VenueDTO) => {
            const venue = createVenueAdapter(venueDTO);
            return venue;
        })
        .catch((error: any) => {
            const translatedError = translateError(
                CREATE_VENUE_ERROR_SPEC,
                error,
            );

            throw new Error(translatedError, {
                ...error,
                translatedError,
            });
        });
};

const translateError = (spec: any, error: any) => {
    const errorKey =
        error.parsedError?.argumentErrors?.INVALID_ARGUMENT ||
        error.parsedError?.error ||
        'default';
    return spec[errorKey] || spec.default;
};

const CREATE_VENUE_ERROR_SPEC = {
    default: () =>
        gettext('Something went wrong. Please try again.').toString(),
};
